import { useEffect, useState, type CSSProperties } from "react";
import { NativeImage } from "@/components/native/NativeImage";
import { useNativePlatform } from "@/hooks/useNativePlatform";

interface ListingCardThumbnailProps {
  src: string;
  hoverSrc?: string;
  alt: string;
  className?: string;
  imgClassName?: string;
  imgStyle?: CSSProperties;
  loading?: "lazy" | "eager";
  /** When true (e.g. whole card hovered), start loading the hover PREVIEW. */
  preloadHover?: boolean;
}

/**
 * Card thumbnail with optional second exterior PREVIEW on desktop hover.
 * Parent should use `group` + pass `preloadHover` when the whole card is hovered.
 */
export function ListingCardThumbnail({
  src,
  hoverSrc,
  alt,
  className,
  imgClassName,
  imgStyle,
  loading = "lazy",
  preloadHover = false,
}: ListingCardThumbnailProps) {
  const { isNative } = useNativePlatform();
  const [hoverLoadedSrc, setHoverLoadedSrc] = useState<string | null>(null);
  const [hoverReady, setHoverReady] = useState(false);

  const canHover = Boolean(hoverSrc) && !isNative;

  useEffect(() => {
    if (!preloadHover || !canHover || !hoverSrc) return;
    if (hoverLoadedSrc === hoverSrc) return;
    setHoverLoadedSrc(hoverSrc);
  }, [preloadHover, canHover, hoverSrc, hoverLoadedSrc]);

  return (
    <div className={className} data-listing-hero-image>
      <NativeImage
        src={src}
        alt={alt}
        className={imgClassName}
        style={imgStyle}
        loading={loading}
      />
      {canHover && hoverLoadedSrc && (
        <NativeImage
          src={hoverLoadedSrc}
          alt=""
          aria-hidden
          className={`${imgClassName ?? ""} absolute inset-0 transition-opacity duration-200 ${
            hoverReady ? "opacity-0 group-hover:opacity-100" : "opacity-0"
          }`}
          style={imgStyle}
          loading="lazy"
          onLoad={() => setHoverReady(true)}
        />
      )}
    </div>
  );
}
